<template>
  <div class="property-list-layout h-screen flex flex-col">
    <Header 
      title="Properties" 
      :showSearch="true" 
      :showMenuToggle="true" 
      @toggle-sidebar="toggleSidebar"
      @search="handleSearch"
    />
    
    <div class="flex flex-1 overflow-hidden">
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-6"
      >
        <LoadingSpinner :loading="loading" message="Loading properties..." />
        <Toast ref="toast" />

        <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span class="block sm:inline">{{ error }}</span>
        </div>

        <div class="mb-6 flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-800">Properties</h2>
          
          <div class="flex space-x-2">
            <button 
              @click="viewMode = 'table'" 
              class="px-3 py-2 rounded-lg"
              :class="{ 'bg-primary-600 text-white': viewMode === 'table', 'bg-white': viewMode !== 'table' }"
            >
              <i class="fas fa-list"></i>
            </button>
            <button 
              @click="viewMode = 'map'" 
              class="px-3 py-2 rounded-lg"
              :class="{ 'bg-primary-600 text-white': viewMode === 'map', 'bg-white': viewMode !== 'map' }"
            >
              <i class="fas fa-map-marked-alt"></i>
            </button>
          </div>
        </div>
        
        <!-- Table View -->
        <div v-if="viewMode === 'table'" class="property-table-view">
          <PropertyTable 
            :loading="propertiesStore.loading"
            @view-property="viewProperty"
            @edit-property="editProperty"
            @delete-property="confirmDeleteProperty"
            @add-property="showAddPropertyModal = true"
          />
        </div>
        
        <!-- Map View -->
        <div v-else-if="viewMode === 'map'" class="property-map-view bg-white rounded-lg shadow-sm p-4 h-[calc(100vh-160px)]">
          <MapContainer />
        </div>
        
        <!-- Add Property Modal -->
        <div v-if="showAddPropertyModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div class="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div class="p-6">
              <div class="flex justify-between items-center mb-6">
                <h3 class="text-xl font-bold">Add New Property</h3>
                <button @click="showAddPropertyModal = false" class="text-gray-500 hover:text-gray-700">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <form @submit.prevent="addProperty">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input 
                      v-model="propertyForm.title"
                      type="text" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input 
                      v-model="propertyForm.address"
                      type="text" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                    <input 
                      v-model.number="propertyForm.price"
                      type="number" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Size (sqft)</label>
                    <input 
                      v-model.number="propertyForm.size"
                      type="number" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <select 
                      v-model="propertyForm.payment_status"
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                    </select>
                  </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                    <input 
                      v-model.number="propertyForm.bedrooms"
                      type="number" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                    <input 
                      v-model.number="propertyForm.bathrooms"
                      type="number" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Year Built</label>
                    <input 
                      v-model.number="propertyForm.year_built"
                      type="number" 
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                    <input 
                      v-model.number="propertyForm.latitude"
                      type="number" 
                      step="0.000001"
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                    <input 
                      v-model.number="propertyForm.longitude"
                      type="number" 
                      step="0.000001"
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                  </div>
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                  <input 
                    v-model="propertyForm.owner_name"
                    type="text" 
                    class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea 
                    v-model="propertyForm.description"
                    class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="3"
                  ></textarea>
                </div>
                
                <div class="flex justify-end space-x-3 mt-6">
                  <button 
                    type="button"
                    @click="showAddPropertyModal = false"
                    class="px-4 py-2 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    :disabled="propertiesStore.loading"
                  >
                    <span v-if="propertiesStore.loading">
                      <i class="fas fa-spinner fa-spin mr-2"></i>Saving...
                    </span>
                    <span v-else>Save Property</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div class="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div class="p-6">
              <h3 class="text-xl font-bold mb-4">Confirm Deletion</h3>
              <p class="mb-6">Are you sure you want to delete this property? This action cannot be undone.</p>
              <div class="flex justify-end space-x-3">
                <button 
                  @click="showDeleteModal = false"
                  class="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button 
                  @click="deleteProperty"
                  class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  :disabled="propertiesStore.loading"
                >
                  <span v-if="propertiesStore.loading">
                    <i class="fas fa-spinner fa-spin mr-2"></i>Deleting...
                  </span>
                  <span v-else>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, inject } from 'vue';
import Header from '../components/Header.vue';
import Sidebar from '../components/Sidebar.vue';
import PropertyTable from '../components/PropertyTable.vue';
import MapContainer from '../components/MapContainer.vue';
import { usePropertiesStore } from '../stores/properties';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import Toast from '../components/Toast.vue';

const propertiesStore = usePropertiesStore();
const sidebarCollapsed = ref(false);
const viewMode = ref('table');
const showAddPropertyModal = ref(false);
const showDeleteModal = ref(false);
const propertyToDeleteId = ref(null);
const loading = ref(true);
const error = ref(null);
const toast = inject('toast');

// Form for adding/editing properties
const propertyForm = reactive({
  title: '',
  address: '',
  price: 0,
  size: 0,
  payment_status: 'unpaid',
  bedrooms: 1,
  bathrooms: 1,
  year_built: 2000,
  latitude: 30.2672,
  longitude: -97.7431,
  owner_name: '',
  description: ''
});

// Methods
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

const handleSearch = (query) => {
  console.log('Search query:', query);
  // Filter properties based on search query
};

const viewProperty = (id) => {
  console.log('View property:', id);
  // Navigate to property details page
};

const editProperty = (id) => {
  console.log('Edit property:', id);
  // Load property data into form and show edit modal
};

const confirmDeleteProperty = (id) => {
  propertyToDeleteId.value = id;
  showDeleteModal.value = true;
};

const deleteProperty = async () => {
  if (!propertyToDeleteId.value) return;
  
  try {
    await propertiesStore.deleteProperty(propertyToDeleteId.value);
    showDeleteModal.value = false;
  } catch (error) {
    console.error('Error deleting property:', error);
  }
};

const addProperty = async () => {
  try {
    await propertiesStore.addProperty(propertyForm);
    showAddPropertyModal.value = false;
    
    // Reset form
    Object.assign(propertyForm, {
      title: '',
      address: '',
      price: 0,
      size: 0,
      payment_status: 'unpaid',
      bedrooms: 1,
      bathrooms: 1,
      year_built: 2000,
      latitude: 30.2672,
      longitude: -97.7431,
      owner_name: '',
      description: ''
    });
  } catch (error) {
    console.error('Error adding property:', error);
  }
};

const fetchProperties = async () => {
  loading.value = true;
  error.value = null;
  try {
    await propertiesStore.fetchProperties();
    toast.value?.addToast('Properties updated', 'success');
  } catch (err) {
    error.value = err.message || 'Failed to load properties';
    toast.value?.addToast(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

const refreshProperties = async () => {
  await fetchProperties();
};

onMounted(() => {
  fetchProperties();
});
</script>
